import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface SimpleModeContextType {
  isSimpleMode: boolean;
  setSimpleMode: (enabled: boolean) => void;
  toggleSimpleMode: () => void;
}

const SimpleModeContext = createContext<SimpleModeContextType | undefined>(undefined);

const STORAGE_KEY = 'agxl-simple-mode';

export function SimpleModeProvider({ children }: { children: ReactNode }) {
  const [isSimpleMode, setIsSimpleMode] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      // Default to true (simple mode ON) for new users
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    // Sync class with document.body
    if (isSimpleMode) {
      document.body.classList.add('simple-mode');
    } else {
      document.body.classList.remove('simple-mode');
    }

    // Persist to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, String(isSimpleMode));
    } catch {
      // Ignore storage errors
    }
  }, [isSimpleMode]);

  const setSimpleMode = useCallback((enabled: boolean) => {
    setIsSimpleMode(enabled);
  }, []);

  const toggleSimpleMode = useCallback(() => {
    setIsSimpleMode(prev => !prev);
  }, []);

  return (
    <SimpleModeContext.Provider value={{ isSimpleMode, setSimpleMode, toggleSimpleMode }}>
      {children}
    </SimpleModeContext.Provider>
  );
}

export function useSimpleMode(): SimpleModeContextType {
  const context = useContext(SimpleModeContext);
  if (context === undefined) {
    throw new Error('useSimpleMode must be used within a SimpleModeProvider');
  }
  return context;
}
